import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ingredients } from '@/data/ingredients';
import { ING } from '@/constants/testIds';

export default function HeroIngredients() {
  return (
    <section
      id="ingredients"
      data-testid={ING.root}
      className="relative py-24 md:py-32 bg-[#0D3A24] text-[#FAF4E3] overflow-hidden"
    >
      <div className="blob absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#2D8B5B]" />
      <div className="blob absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#1D7448]" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs uppercase tracking-[0.24em] text-[#FAF4E3]/60 mb-4">
              Hero Ingredients
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#FAF4E3] font-light leading-[1.05] max-w-3xl">
              The herbs behind <span className="italic">every ritual.</span>
            </h2>
          </motion.div>
          <Link
            to="/ingredients"
            data-testid={ING.dictLink}
            className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#FAF4E3] group"
          >
            <span className="link-sweep">Ingredient dictionary</span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
          {ingredients.map((ing, i) => (
            <motion.div
              key={ing.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="flip-card aspect-[4/5] rounded-[24px]"
            >
              <Link
                to="/ingredients"
                data-testid={ING.card(ing.slug)}
                className="block w-full h-full rounded-[24px]"
              >
                <div className="flip-card-inner rounded-[24px]">
                  <div className="flip-card-front">
                    <img
                      src={ing.image}
                      alt={ing.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D3A24]/80 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5 right-5">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#FAF4E3]/70">
                        {ing.origin}
                      </p>
                      <h3 className="font-serif text-2xl md:text-3xl text-[#FAF4E3] leading-tight mt-1">
                        {ing.name}
                      </h3>
                    </div>
                  </div>
                  <div className="flip-card-back bg-[#1D7448] p-6 md:p-7 flex flex-col justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#FAF4E3]/70">
                        {ing.short}
                      </p>
                      <h3 className="font-serif text-2xl md:text-3xl text-[#FAF4E3] leading-tight mt-1">
                        {ing.name}
                      </h3>
                      <p className="text-sm text-[#FAF4E3]/85 leading-relaxed mt-4">
                        {ing.benefit}
                      </p>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FAF4E3]">
                      Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
